/**
 * SoleadLab 2.0 — Evidence Store
 *
 * B3.1: Zustand store for evidence entity consumption.
 *
 * Read-only store — evidence items are immutable (write-once on backend).
 * Site-scoped: call setSiteId to switch sites, which clears stale items
 * and triggers a fresh fetch.
 *
 * State:
 *   items     — cached evidence items for the current site
 *   loading   — fetch in progress
 *   error     — last fetch error message (null if none)
 *   siteId    — current site scope
 *
 * Actions:
 *   setSiteId(id)   — switch site scope (clears + refetches)
 *   fetchItems()    — load all evidence items for current site
 *   getById(id)     — lookup a cached item by ID
 */

import { create } from 'zustand';
import { fetchEvidenceItems, type EvidenceItem } from '../services/evidenceApi';

interface EvidenceState {
  items: EvidenceItem[];
  loading: boolean;
  error: string | null;
  siteId: string | null;

  setSiteId: (siteId: string) => void;
  fetchItems: (typeFilter?: string) => Promise<void>;
  getById: (id: string) => EvidenceItem | undefined;
  clear: () => void;
}

export const useEvidenceStore = create<EvidenceState>((set, get) => ({
  items: [],
  loading: false,
  error: null,
  siteId: null,

  setSiteId: (siteId: string) => {
    const current = get().siteId;
    if (current === siteId) return;
    set({ siteId, items: [], error: null });
  },

  fetchItems: async (typeFilter?: string) => {
    const { siteId } = get();
    if (!siteId) return;

    set({ loading: true, error: null });
    try {
      const result = await fetchEvidenceItems(siteId, typeFilter);
      set({ items: result.items, loading: false });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch evidence',
      });
    }
  },

  getById: (id: string) => {
    return get().items.find(item => item.id === id);
  },

  clear: () => {
    set({ items: [], siteId: null, error: null, loading: false });
  },
}));
